import { create } from 'zustand'
import { DEMO_EMPLOYEE_ID, getMonday, toIsoDate } from '../services/timesheetDataService'

interface TimesheetState {
  currentEmployeeId: string
  setCurrentEmployeeId: (id: string) => void
  /**
   * Hosted app on SAP CATS: the personnel number the server resolved for the
   * signed-in person (or the one they are impersonating). While set it wins
   * over any persona switch, so time can only be entered as that person.
   */
  pinnedSapEmployeeId: string | null
  pinSapEmployee: (pernr: string | null) => void

  weekStart: string
  setWeekStart: (d: string) => void
  prevWeek: () => void
  nextWeek: () => void

  pendingCount: number
  setPendingCount: (n: number) => void

  /**
   * The week the worker has explicitly reopened to adjust, if any.
   *
   * A submitted week is otherwise read-only. Adjusting one is a deliberate act,
   * not a side effect of clicking a cell, so it is held here: the grid unlocks
   * its cells and gates every change behind a reason, and the submit control
   * turns into "Re-submit for approval" so the change actually reaches the
   * approver instead of quietly diverging from what they signed off.
   *
   * Deliberately NOT persisted - reopening a week should not survive a refresh.
   */
  adjustingWeek: string | null
  setAdjustingWeek: (weekStartIso: string | null) => void
}

// Persist the picked persona so a refresh keeps the active employee instead of
// snapping back to the DEMO_EMPLOYEE_ID default. Demo-only impersonation state;
// safe to keep in localStorage.
const EMPLOYEE_ID_KEY = 'ppm-timesheet-employee-id'

function loadPersistedEmployeeId(): string {
  try {
    const stored = localStorage.getItem(EMPLOYEE_ID_KEY)
    if (stored) return stored
  } catch { /* ignore disabled storage */ }
  return DEMO_EMPLOYEE_ID
}

export const useTimesheetStore = create<TimesheetState>((set, get) => ({
  currentEmployeeId: loadPersistedEmployeeId(),
  setCurrentEmployeeId: (id) => {
    const pinned = get().pinnedSapEmployeeId
    if (pinned && id !== pinned) return
    try { localStorage.setItem(EMPLOYEE_ID_KEY, id) } catch { /* ignore */ }
    set({ currentEmployeeId: id })
  },

  pinnedSapEmployeeId: null,
  pinSapEmployee: (pernr) => {
    // Not persisted: the server re-resolves it on every start.
    set(pernr ? { pinnedSapEmployeeId: pernr, currentEmployeeId: pernr } : { pinnedSapEmployeeId: null })
  },

  weekStart: toIsoDate(getMonday(new Date())),
  setWeekStart: (d) => set({ weekStart: d }),
  prevWeek: () => {
    const d = new Date(get().weekStart)
    d.setDate(d.getDate() - 7)
    set({ weekStart: toIsoDate(d) })
  },
  nextWeek: () => {
    const d = new Date(get().weekStart)
    d.setDate(d.getDate() + 7)
    set({ weekStart: toIsoDate(d) })
  },

  pendingCount: 2,
  setPendingCount: (n) => set({ pendingCount: n }),

  adjustingWeek: null,
  setAdjustingWeek: (weekStartIso) => set({ adjustingWeek: weekStartIso }),
}))
